
import { CommonModule } from "@angular/common";
import { Component } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatTableModule } from "@angular/material/table";
import { RouterModule } from "@angular/router";
import { UserProfilesSearcQueryResponseInfo } from "@models/user-profiles.model"
import { CustomerService } from "@services/customer/customer-service";
import { InputLimit } from "@utils/input-limit";
import { JapaneseDateformat } from "@utils/japanese-date-format";
import { JapaneseLabels } from "@utils/japaneseLabels";





/**
 * CustomerList component displays a paginated list of customers.
 * Handles pagination, limit changes, and data fetching from the CustomerService.
 */
@Component({
  selector: "app-customer-list",
  standalone: true,
  imports: [CommonModule, RouterModule, MatTableModule, FormsModule],
  templateUrl: "./customer-list.html",
  styleUrl: "./customer-list.scss",
})
export class CustomerList {
  /** Input limit utility instance */
  inputLimit = new InputLimit();

  /** Loading state for data fetching */
  loading: boolean = false;

  /** IntersectionObserver for lazy loading or infinite scroll (not used in this snippet) */
  observer!: IntersectionObserver;

  /** japan  text labels */
  japaneseLabel = JapaneseLabels;

  /**
   * Angular lifecycle hook.
   * Fetches the initial customer list on component initialization.
   */
  ngOnInit() {
    this.doList();
  }

  /**
   * Constructor injects the CustomerService.
   * 
   * @param customerService Service for fetching customer data
   */
  constructor(private customerService: CustomerService) { }

  /** Current page number (for pagination) */
  page: number = 1;

  /** Number of items per page */
  limit: number = 10;

  /** List of customers for the current page */
  customerList: UserProfilesSearcQueryResponseInfo[] = [];

  /** Total number of customers */
  totalCount: number = 0;

  /** Total number of pages */
  totalPages: number = 0;

  /** Currently selected page */
  currentPage: number = 1;
  search_field!: string // search phone number input property
  /**
   * Fetches the customer list for the current page and limit.
   * Updates customerList, totalCount, and totalPages.
   */
  doList() {
    this.loading = true;
    this.customerService.getCustomers(this.currentPage, this.limit, this.search_field).subscribe({
      next: (response) => {
        this.customerList = response.profiles.map(customer => ({
          ...customer,
          date_of_birth: customer.date_of_birth
            ? JapaneseDateformat(customer.date_of_birth) || undefined
            : undefined
        }));
        this.totalCount = response.total_count;
        this.totalPages = Math.ceil(this.totalCount / this.limit);
      },
      error: (err) => {
        console.error("Error", err);
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  /**
   * Calculates the start index of the current page.
   * 
   * @returns The starting index (1-based)
   */
  get startIndex(): number {
    return (this.currentPage - 1) * this.limit + 1;
  }

  /**
   * Calculates the end index of the current page.
   * 
   * @returns The ending index (1-based)
   */
  get endIndex(): number {
    return Math.min(this.currentPage * this.limit, this.totalCount);
  }

  /**
   * Generates an array of page numbers for pagination controls.
   * 
   * @returns Array of page numbers
   */
  get pages(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  /**
   * Navigates to a specific page if within valid range.
   * 
   * @param page The page number to navigate to
   */
  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.doList();
    }
  }

  // /**
  //  * Navigates to the previous page if not on the first page.
  //  */
  // previousPage() {
  //   if (this.currentPage > 1) {
  //     this.currentPage--;
  //     this.doList();
  //   }
  // }

  /**
   * Navigates to the next page if not on the last page.
   */
  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.doList();
    }
  }

  /**
   * Handles changes to the items-per-page limit.
   * Resets to the first page and fetches new data.
   * 
   * @param event The change event from the limit select element
   */
  onLimitChange(event: Event) {
    const value: string = (event.target as HTMLSelectElement).value;
    this.limit = +value; // convert to number
    this.currentPage = 1; // reset to first page
    this.doList();
  }
}